import { App, Button } from 'antd';
import { Copy } from 'lucide-react';
import DocMarkdown from '@/components/DocMarkdown';
import { copyToClipboard } from '@/utils/utils';
import { BRIDGE_CONNECTION_URL } from '../ChannelAccountComponents/helpers';

/** 由 WebSocket 地址推导同源的 HTTP 上行地址前缀（两个平面同一服务）。 */
function httpBaseFrom(wsUrl: string): string {
  return wsUrl
    .replace(/^wss:\/\//, 'https://')
    .replace(/^ws:\/\//, 'http://')
    .replace(/\/ws\/v1\/?$/, '/api/bridge/v1/');
}

const HTTP_BASE_URL = httpBaseFrom(BRIDGE_CONNECTION_URL);

const AUTH_DOC = `## 两个平面

WebSocket 渠道由两个端点组成，职责分离：

| 平面 | 地址 | 方向 | 用途 |
| --- | --- | --- | --- |
| **HTTP** | \`${HTTP_BASE_URL}{Method}\` | 上行 | 发消息、响应中断、取消、查历史、查身份 |
| **WebSocket** | \`${BRIDGE_CONNECTION_URL}\` | **纯下行** + 订阅控制 | 事件推送 |

WebSocket 只用于接收事件，上行仅保留 \`sub\` / \`unsub\` / \`PING\` 三种幂等帧；
所有写操作走 HTTP，因此每次发送都有明确的成功/失败返回值，且不会阻塞事件接收。

两个平面使用**同一个 Token**。

## 鉴权方式

Token 在 InfiniAgent 控制台创建（类型 \`WEBSOCKET\`），形如 \`wst_xxx\`，
明文仅在创建时返回一次。

### WebSocket：Subprotocol 携带 Token

通过握手的 \`Sec-WebSocket-Protocol\` 请求头携带 Token，在 HTTP Upgrade 阶段完成鉴权。

\`\`\`text
Sec-WebSocket-Protocol: infini.bridge.v2, bearer.<base64url(token)>
\`\`\`

- 第一段：\`infini.bridge.v2\`（端点契约标识，服务端会在 101 响应中回写协商结果）
- 第二段：\`bearer.\` 前缀 + Token 的 base64url 编码（推荐无 padding，带 padding 亦可）

浏览器的 WebSocket API 无法自定义请求头，这是在握手中携带凭证的标准方式：

\`\`\`js
new WebSocket(url, [
  'infini.bridge.v2',
  'bearer.' + btoa(token).replace(/\\+/g, '-').replace(/\\//g, '_').replace(/=+$/, ''),
]);
\`\`\`

非浏览器客户端也可改用 \`Authorization: Bearer <token>\` 请求头，两者等价。

### HTTP：Authorization 头携带 Token

\`\`\`text
Authorization: Bearer wst_xxx
\`\`\`

### 握手时序

\`\`\`text
Client                                      Server
  │                                            │
  │──── HTTP Upgrade + Sec-WebSocket-Protocol ▶│
  │                              [校验 token]   │
  │◀─── 101 Switching Protocols ───────────────│
  │       Sec-WebSocket-Protocol: infini.bridge.v2
  │                                            │
  │◀─── {"t":"hello","d":{conn_id,server_time}}│
  │       ✓ 已鉴权，可发 sub 订阅会话             │
\`\`\`

### 握手响应与重连策略

鉴权在 Upgrade **之前**完成，失败会返回**真实的 HTTP 状态码**而非先升级再发错误帧，
便于客户端据此决定是否重连：

| 响应 | 含义 | 客户端应对 |
| --- | --- | --- |
| \`101\` | 握手成功，随后收到 \`hello\` 帧 | 发送 \`sub\` 订阅会话 |
| \`401\` | Token 无效 / 过期 / 已吊销 / 无账号绑定 | **停止重连**，更换 Token |
| \`503\` | 服务暂不可达 | **退避重连** |

响应体形如 \`{"code":"token_expired","message":"..."}\`，\`code\` 可用于细分提示。

> Token 吊销后最长约 1 分钟生效（校验结果缓存）。

### 安全须知

- ⚠️ **不支持通过 query string 传 Token** —— 那会让 Token 落入接入层访问日志与浏览器历史；
- ⚠️ Token **长期有效**，请按密钥管理：不要提交进代码库，不要硬编码进前端产物；
- 浏览器发起的握手按 \`Origin\` 白名单校验，未在白名单内的来源会被拒绝（防止跨站 WebSocket 劫持）；无 \`Origin\` 头的请求（非浏览器客户端）不受此限制。

## 连接之后

### 订阅制

下行是**订阅制**的 —— 不发 \`sub\` 就收不到任何事件。
\`SendMessage\` 返回的 \`context_key\` 即订阅键：

\`\`\`json
{"t":"sub","d":{"session_id":"<context_key>","since_seq":0}}
\`\`\`

服务端以 \`subd\` 帧回执，随后持续推送 \`ev\` 事件帧。
断线重连时带上最后收到的外层 \`seq\` 作为 \`since_seq\` 即可续传。

### 心跳

- 服务端每 **30 秒**发一次 WebSocket 协议级 ping 帧，标准 WebSocket 实现会自动回应 pong，
  **客户端不需要写任何代码**；
- 客户端若想主动确认连接可用，可发 \`{"t":"PING"}\`，服务端立即回 \`{"t":"PONG"}\`；
- 连续 **2 个周期（60 秒）**未收到任何入站消息即判定连接失效并断开 ——
  协议级 pong、业务帧、\`sub\` 都算入站消息。

### 连接数限制

单个渠道账号最多 **10 条**并发连接（按账号统计，多签 Token 不能绕过）。
超限时服务端下发 \`{"t":"err","d":{"reason":"too_many_connections"}}\` 并关闭连接，
客户端应退避后重连。
`;

/** 连接信息 tab：连接地址 + 鉴权方式说明 + 安全控制。 */
export default function ConnectionInfoTab() {
  const { message } = App.useApp();

  async function copy(text: string) {
    const ok = await copyToClipboard(text);
    if (ok) message.success('已复制');
    else message.error('复制失败');
  }

  return (
    <>
      <div className="conn-url">
        <header>
          <b>连接地址</b>
          <Button
            size="small"
            icon={<Copy size={12} />}
            onClick={() => copy(BRIDGE_CONNECTION_URL)}
          >
            复制
          </Button>
        </header>
        <code className="pp-mono url">{BRIDGE_CONNECTION_URL}</code>
      </div>

      <div className="conn-url" style={{ marginTop: 12 }}>
        <header>
          <b>HTTP 上行地址</b>
          <Button size="small" icon={<Copy size={12} />} onClick={() => copy(HTTP_BASE_URL)}>
            复制
          </Button>
        </header>
        <code className="pp-mono url">{HTTP_BASE_URL}</code>
      </div>

      <div className="auth-doc" style={{ marginTop: 16 }}>
        <DocMarkdown docs={{ 'auth.md': AUTH_DOC }} docPath="auth.md" />
      </div>
    </>
  );
}
